import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useGetInitialModel from '@sdk/shifts/hooks/useGetInitialModel';
import { DURATION_UNIT_OPTIONS } from '@sdk/common/constants';
import { AlarmUnits } from '@sdk/shifts/types/shiftAlarmsType';
import type { ShiftAlarmModelType, ShiftAlarmsModel } from '@sdk/shifts/types/shiftAlarmsType';

import { useSettingsErrorsContext } from '../../../SettingsErrorContext';
import { TabType } from '../../../types';
import { useUserSettings } from '../../../UserSettingsContext';

const SHIFT_ALARMS_LIMIT = 3;

export type UseShiftAlarmsType = {
  alarmsModel: ShiftAlarmsModel;
  addAnotherAlarm: () => void;
  setAlarmsModel: (alarmsModel: ShiftAlarmsModel) => void;
  canAddAnotherAlarm: () => boolean;
  removeAlarm: (alarmId: string) => void;
  onChangeAlarmUnit: (value: AlarmUnits, alarmId: string) => void;
  onChangeAlarmValue: (value: string | number, alarmId: string) => void;
};

export const useShiftAlarms = (): UseShiftAlarmsType => {
  const { userSettings, onUserSettingsChange, initialUserSettings } = useUserSettings();
  const didMountRef = useRef(false);
  const alarmsInitailModel = useGetInitialModel(userSettings);
  const [alarmsModel, setAlarmsModel] = useState(alarmsInitailModel);
  const { toggleError } = useSettingsErrorsContext();
  const deafultUnit = DURATION_UNIT_OPTIONS.find((option) => option.value === AlarmUnits.minutes);

  const getDefaultAlarm = (): ShiftAlarmModelType => {
    return {
      id: uuidv4(),
      value: null,
      unit: { ...deafultUnit },
    };
  };

  useEffect(() => {
    if (didMountRef.current) {
      initialUserSettings.shiftAlarms === userSettings.shiftAlarms
        ? setAlarmsModel(alarmsInitailModel)
        : null;
    } else {
      didMountRef.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings.shiftAlarms]);

  const addAnotherAlarm = (): void => {
    setAlarmsModel([...alarmsModel, getDefaultAlarm()]);
  };

  const canAddAnotherAlarm = (): boolean => {
    return alarmsModel.length < SHIFT_ALARMS_LIMIT;
  };

  const removeAlarm = (alarmId: string): void => {
    const updatedAlarms = alarmsModel.filter((alarmModel) => alarmModel.id !== alarmId);
    setAlarmsModel(updatedAlarms);
    toggleError(TabType.notifications, alarmId, false);
  };

  const setDuration = (duration: number, unit: AlarmUnits): number => {
    return unit === AlarmUnits.hours ? duration * 60 : duration;
  };

  const getDuration = (duration: number, unit: AlarmUnits): number => {
    return unit === AlarmUnits.hours ? duration * 60 : Math.ceil(duration / 60);
  };

  const onChangeAlarmUnit = (value: AlarmUnits, alarmId: string): void => {
    const durationUnit = DURATION_UNIT_OPTIONS.find((option) => option.value === value);
    const updatedAlarmsModel = alarmsModel.map((alarmModel) => {
      return alarmModel.id === alarmId
        ? {
            ...alarmModel,
            unit: durationUnit,
            value: getDuration(Number(alarmModel.value), value),
          }
        : alarmModel;
    });

    const shiftAlarmsUnits = updatedAlarmsModel.map((alarmModel) => alarmModel.unit.value);
    onUserSettingsChange({ clientFlags: { ...userSettings.clientFlags, shiftAlarmsUnits } });
    setAlarmsModel(updatedAlarmsModel);
  };

  const onChangeAlarmValue = (value: string | number, alarmId: string): void => {
    const updatedAlarmsModel = alarmsModel.map((alarmModel) =>
      alarmModel.id === alarmId
        ? {
            ...alarmModel,
            value: setDuration(Number(value), alarmModel.unit.value),
          }
        : alarmModel
    );

    setAlarmsModel(updatedAlarmsModel);
  };

  return {
    alarmsModel,
    addAnotherAlarm,
    setAlarmsModel,
    canAddAnotherAlarm,
    removeAlarm,
    onChangeAlarmUnit,
    onChangeAlarmValue,
  };
};
